/**
 * ScrollToTop - Scrolls to top on route change, or to a hash target
 *
 * Uses location.key as a dependency so the effect fires on every
 * navigation, including repeated clicks to the same hash URL.
 * Retries the element lookup to handle lazy-loaded pages.
 */
import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_RETRIES = 10;
const RETRY_INTERVAL = 80;

const ScrollToTop: React.FC = () => {
  const { pathname, hash, key } = useLocation();

  const scrollToHash = useCallback((id: string, attempt = 0) => {
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth' }));
      return;
    }
    if (attempt < MAX_RETRIES) {
      setTimeout(() => scrollToHash(id, attempt + 1), RETRY_INTERVAL);
    }
  }, []);

  useEffect(() => {
    if (hash) {
      scrollToHash(hash.slice(1));
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [key, scrollToHash]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default ScrollToTop;
